import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Container from '../layout/Container';
import SectionTitle from '../ui/SectionTitle';
import Input from '../ui/Input';
import TextArea from '../ui/TextArea';
import Button from '../ui/Button';
import { publicApi } from '../../services/publicApi';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    content: '',
    phone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [contactInfo, setContactInfo] = useState({
    email: 'info@raadsotech.com',
    phone: '+252 61 1178748',
    address: 'Howlwadag, Waberi, Mogadishu',
  });

  useEffect(() => {
    let isMounted = true;
    const loadContactInfo = async () => {
      try {
        const data = await publicApi.getContactInformation();
        const selected = Array.isArray(data) ? data[0] : null;
        if (!selected || !isMounted) return;

        setContactInfo((prev) => ({
          ...prev,
          email: selected.email || prev.email,
          phone: selected.phone || prev.phone,
          address: selected.address || prev.address,
        }));
      } catch {
        // Keep fallback contact info.
      }
    };

    loadContactInfo();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus({ type: '', message: '' });
    try {
      await publicApi.sendMessage({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        content: formData.content,
        isRead: false,
      });

      setStatus({ type: 'success', message: 'Message sent successfully. We will contact you soon.' });
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        subject: '',
        content: '',
        phone: '',
      });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Failed to send message.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container id="contact" className="bg-white dark:bg-black transition-colors duration-300">
      <SectionTitle 
        title="Get in Touch" 
        subtitle="Contact Us" 
      />
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mt-12">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="text-3xl font-bold text-black dark:text-white mb-6">Let's build something amazing together.</h3>
          <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white/70 dark:bg-[#101010] p-4 mb-4">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Direct Contact</p>
            <p className="text-sm font-semibold text-black dark:text-white">{contactInfo.email}</p>
            <p className="text-sm font-semibold text-black dark:text-white">{contactInfo.phone}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{contactInfo.address}</p>
          </div>
          <div className="w-full mt-4">
            <img 
              src="/img/contact image.png" 
              alt="Contact Us" 
              className="w-full h-auto object-contain"
            />
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="glass-card p-8 bg-gray-50/50 dark:bg-[#0f0f0f]/80 dark:border-white/10"
        >
          <form onSubmit={handleSubmit} className="flex flex-col gap-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="First Name" id="firstName" placeholder="John" value={formData.firstName} onChange={handleChange('firstName')} required />
              <Input label="Last Name" id="lastName" placeholder="Doe" value={formData.lastName} onChange={handleChange('lastName')} required />
            </div>
            <Input label="Email Address" id="email" type="email" placeholder="john@example.com" value={formData.email} onChange={handleChange('email')} required />
            <Input label="Phone Number" id="phone" placeholder="+252..." value={formData.phone} onChange={handleChange('phone')} />
            <Input label="Subject" id="subject" placeholder="Project Inquiry" value={formData.subject} onChange={handleChange('subject')} required />
            <TextArea label="Message" id="message" placeholder="Tell us about your project..." value={formData.content} onChange={handleChange('content')} required />
            {status.message ? (
              <p className={`text-sm ${status.type === 'success' ? 'text-green-500' : 'text-red-500'}`}>
                {status.message}
              </p>
            ) : null}
            <Button variant="primary" className="w-full mt-4 py-4 text-lg" disabled={submitting}>
              {submitting ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
        </motion.div>
      </div>
    </Container>
  );
};

export default Contact;
